/**
 * MorningHarvest Player
 * Audio-First Daily Briefing - Like Spotify for your law practice
 *
 * Persistent footer player with:
 * - Waveform visualization
 * - Voice commands
 * - Zen Mode
 */

import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Mic,
  MicOff,
  Moon,
  Sun,
  Volume2,
  VolumeX,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  Clock,
  DollarSign,
  FileText,
} from 'lucide-react';
import type {
  MorningHarvestProps,
  PlayerState,
  BriefingSegment,
  SegmentType,
} from '../../types/audio';
import { useVoiceCommands, type VoiceCommandResult } from '../../hooks/useVoiceCommands';
import { useHapticFeedback } from '../../hooks/useHapticFeedback';
import { useLawityContext } from '../../context/LawityContext';

// Segment type icons
const SEGMENT_ICONS: Record<SegmentType, ReactNode> = {
  summary: <FileText className="w-4 h-4" />,
  task: <CheckCircle2 className="w-4 h-4" />,
  alert: <AlertTriangle className="w-4 h-4" />,
  insight: <Lightbulb className="w-4 h-4" />,
  deadline: <Clock className="w-4 h-4" />,
  billing: <DollarSign className="w-4 h-4" />,
};

// Waveform visualization
interface WaveformProps {
  isPlaying: boolean;
  progress: number;
  barCount?: number;
}

function Waveform({ isPlaying, progress, barCount = 40 }: WaveformProps) {
  const bars = Array.from({ length: barCount }, (_, i) => {
    const baseHeight = 20 + Math.sin(i * 0.5) * 15 + Math.cos(i * 0.3) * 10;
    const isPlayed = i / barCount < progress;

    return (
      <motion.div
        key={i}
        className={`w-1 rounded-full ${
          isPlayed ? 'bg-green-500' : 'bg-neutral-300 dark:bg-neutral-600'
        }`}
        animate={{
          height: isPlaying
            ? [baseHeight, baseHeight + Math.random() * 20, baseHeight]
            : baseHeight,
        }}
        transition={{
          duration: 0.3,
          repeat: isPlaying ? Infinity : 0,
          delay: i * 0.02,
        }}
        style={{ minHeight: 8 }}
      />
    );
  });

  return (
    <div className="flex items-center justify-center gap-0.5 h-12">
      {bars}
    </div>
  );
}

// Voice mode button
interface VoiceModeButtonProps {
  isActive: boolean;
  isListening: boolean;
  onClick: () => void;
}

function VoiceModeButton({ isActive, isListening, onClick }: VoiceModeButtonProps) {
  return (
    <motion.button
      className={`
        relative w-12 h-12 rounded-full
        flex items-center justify-center
        transition-colors
        ${isActive
          ? 'bg-green-500 text-white'
          : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
        }
      `}
      onClick={onClick}
      whileTap={{ scale: 0.95 }}
    >
      {isListening && (
        <motion.div
          className="absolute inset-0 rounded-full bg-green-500"
          animate={{
            scale: [1, 1.5],
            opacity: [0.5, 0],
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />
      )}
      {isActive ? (
        <Mic className="w-5 h-5 relative z-10" />
      ) : (
        <MicOff className="w-5 h-5" />
      )}
    </motion.button>
  );
}

// Segment progress dots
interface SegmentIndicatorProps {
  segments: BriefingSegment[];
  currentIndex: number;
  onSegmentClick: (index: number) => void;
}

function SegmentIndicator({ segments, currentIndex, onSegmentClick }: SegmentIndicatorProps) {
  return (
    <div className="flex items-center gap-1">
      {segments.map((segment, i) => (
        <button
          key={segment.id}
          onClick={() => onSegmentClick(i)}
          className={`
            h-1.5 rounded-full transition-all duration-300
            ${i === currentIndex
              ? 'w-6 bg-green-500'
              : i < currentIndex
                ? 'w-2 bg-green-300'
                : 'w-2 bg-neutral-300 dark:bg-neutral-600'
            }
          `}
          title={segment.title}
        />
      ))}
    </div>
  );
}

/**
 * Main Morning Harvest Player
 */
export function MorningHarvest({
  briefing,
  onSegmentAction,
  onComplete,
  autoPlay = false,
  defaultVolume = 0.8,
}: MorningHarvestProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const haptic = useHapticFeedback({ enabled: true });
  const { isZenMode, setZenMode } = useLawityContext();

  const [playerState, setPlayerState] = useState<PlayerState>({
    playback: 'idle',
    currentSegmentIndex: 0,
    currentTime: 0,
    duration: 0,
    volume: defaultVolume,
    isMuted: false,
    isZenMode: false,
    isVoiceActive: false,
  });

  const currentSegment = briefing?.segments[playerState.currentSegmentIndex] ?? null;

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = playerState.duration > 0
    ? playerState.currentTime / playerState.duration
    : 0;

  // Playback controls
  const play = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.play();
      setPlayerState((prev) => ({ ...prev, playback: 'playing' }));
    }
  }, []);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setPlayerState((prev) => ({ ...prev, playback: 'paused' }));
    }
  }, []);

  const togglePlayback = useCallback(() => {
    if (playerState.playback === 'playing') {
      pause();
    } else {
      play();
    }
    haptic.trigger('light');
  }, [playerState.playback, play, pause, haptic]);

  const skipToSegment = useCallback((index: number) => {
    if (!briefing || index < 0 || index >= briefing.segments.length) return;

    setPlayerState((prev) => ({
      ...prev,
      currentSegmentIndex: index,
      currentTime: 0,
    }));
    haptic.trigger('medium');
  }, [briefing, haptic]);

  const nextSegment = useCallback(() => {
    if (!briefing) return;

    const nextIndex = playerState.currentSegmentIndex + 1;
    if (nextIndex >= briefing.segments.length) {
      onComplete();
      setPlayerState((prev) => ({ ...prev, playback: 'idle' }));
    } else {
      skipToSegment(nextIndex);
    }
  }, [briefing, playerState.currentSegmentIndex, onComplete, skipToSegment]);

  const previousSegment = useCallback(() => {
    skipToSegment(Math.max(0, playerState.currentSegmentIndex - 1));
  }, [playerState.currentSegmentIndex, skipToSegment]);

  // Voice command handler
  const handleVoiceCommand = useCallback(
    (result: VoiceCommandResult) => {
      if (!result.command || result.confidence < 0.5) return;

      haptic.trigger('success');

      switch (result.command) {
        case 'handle':
          if (currentSegment) {
            onSegmentAction(currentSegment.id, 'handle');
            nextSegment();
          }
          break;
        case 'skip':
          nextSegment();
          break;
        case 'snooze':
          if (currentSegment) {
            onSegmentAction(currentSegment.id, 'snooze');
            nextSegment();
          }
          break;
        case 'repeat':
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
          }
          break;
        case 'details':
          if (currentSegment) {
            onSegmentAction(currentSegment.id, 'details');
          }
          break;
        case 'pause':
          pause();
          break;
        case 'resume':
          play();
          break;
      }
    },
    [currentSegment, onSegmentAction, nextSegment, pause, play, haptic]
  );

  const voice = useVoiceCommands(handleVoiceCommand, {
    enabled: playerState.isVoiceActive,
    continuous: true,
  });

  const toggleVoiceMode = useCallback(() => {
    setPlayerState((prev) => ({
      ...prev,
      isVoiceActive: !prev.isVoiceActive,
    }));
    haptic.trigger('heavy');
  }, [haptic]);

  const toggleZenMode = useCallback(() => {
    setZenMode(!isZenMode);
    haptic.trigger('medium');
  }, [isZenMode, setZenMode, haptic]);

  const toggleMute = useCallback(() => {
    setPlayerState((prev) => ({
      ...prev,
      isMuted: !prev.isMuted,
    }));
    if (audioRef.current) {
      audioRef.current.muted = !playerState.isMuted;
    }
  }, [playerState.isMuted]);

  // Audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setPlayerState((prev) => ({
        ...prev,
        currentTime: audio.currentTime,
      }));
    };

    const handleLoadedMetadata = () => {
      setPlayerState((prev) => ({
        ...prev,
        duration: audio.duration,
        playback: 'paused',
      }));
    };

    const handleEnded = () => {
      nextSegment();
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [nextSegment]);

  // Auto-play
  useEffect(() => {
    if (autoPlay && briefing && playerState.playback === 'idle') {
      play();
    }
  }, [autoPlay, briefing, playerState.playback, play]);

  if (!briefing) {
    return null;
  }

  return (
    <>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={currentSegment?.audioUrl}
        preload="auto"
      />

      {/* Zen Mode Overlay */}
      <AnimatePresence>
        {isZenMode && (
          <motion.div
            className="fixed inset-0 bg-neutral-900/95 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      {/* Player */}
      <motion.div
        className={`
          fixed bottom-0 left-0 right-0
          bg-white dark:bg-neutral-900
          border-t border-neutral-200 dark:border-neutral-800
          ${isZenMode ? 'z-50' : 'z-40'}
        `}
        animate={{
          height: isZenMode ? 200 : 80,
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <div className="max-w-5xl mx-auto h-full px-4 py-3 flex flex-col justify-center">
          {/* Zen Mode: Expanded View */}
          {isZenMode && currentSegment && (
            <div className="mb-4 text-center">
              <div className="inline-flex items-center gap-2 mb-2">
                <span className={`
                  p-1.5 rounded-lg
                  ${currentSegment.priority === 'high'
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600'
                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600'
                  }
                `}>
                  {SEGMENT_ICONS[currentSegment.type]}
                </span>
                <span className="text-xs text-neutral-400 uppercase tracking-wider">
                  {currentSegment.type}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {currentSegment.title}
              </h3>
              <p className="text-neutral-400 text-sm max-w-lg mx-auto">
                {currentSegment.content.slice(0, 150)}...
              </p>
            </div>
          )}

          {/* Main Controls Row */}
          <div className="flex items-center gap-4">
            {/* Segment Info */}
            <div className={`flex-1 min-w-0 ${isZenMode ? 'hidden sm:block' : ''}`}>
              {currentSegment && (
                <div className="flex items-center gap-3">
                  <div
                    className={`
                      w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                      ${currentSegment.priority === 'high'
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-600'
                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600'
                      }
                    `}
                  >
                    {SEGMENT_ICONS[currentSegment.type]}
                  </div>

                  <div className="min-w-0">
                    <h4 className="font-medium text-neutral-900 dark:text-white truncate text-sm">
                      {currentSegment.title}
                    </h4>
                    <div className="flex items-center gap-3 text-xs text-neutral-500">
                      <span>
                        {formatTime(playerState.currentTime)} / {formatTime(playerState.duration || currentSegment.duration)}
                      </span>
                      <SegmentIndicator
                        segments={briefing.segments}
                        currentIndex={playerState.currentSegmentIndex}
                        onSegmentClick={skipToSegment}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Center Controls */}
            <div className="flex items-center gap-1">
              <button
                onClick={previousSegment}
                className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                disabled={playerState.currentSegmentIndex === 0}
              >
                <SkipBack className="w-5 h-5" />
              </button>

              <motion.button
                onClick={togglePlayback}
                className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center shadow-lg"
                whileTap={{ scale: 0.95 }}
              >
                {playerState.playback === 'playing' ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5 ml-0.5" />
                )}
              </motion.button>

              <button
                onClick={nextSegment}
                className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
              >
                <SkipForward className="w-5 h-5" />
              </button>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              {/* Volume */}
              <button
                onClick={toggleMute}
                className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
              >
                {playerState.isMuted ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>

              {/* Voice Mode */}
              <VoiceModeButton
                isActive={playerState.isVoiceActive}
                isListening={voice.isListening}
                onClick={toggleVoiceMode}
              />

              {/* Zen Mode */}
              <button
                onClick={toggleZenMode}
                className={`
                  p-2 rounded-lg transition-colors
                  ${isZenMode
                    ? 'bg-green-500 text-white'
                    : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
                  }
                `}
                title="Zen Mode"
              >
                {isZenMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Waveform (zen mode or playing) */}
          <AnimatePresence>
            {(isZenMode || playerState.playback === 'playing') && (
              <motion.div
                className="mt-3"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 48 }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Waveform
                  isPlaying={playerState.playback === 'playing'}
                  progress={progress}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Voice Command Hint */}
          <AnimatePresence>
            {playerState.isVoiceActive && (
              <motion.div
                className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: -8 }}
                exit={{ opacity: 0, y: 10 }}
              >
                <div className="bg-neutral-800 text-white text-xs px-3 py-1.5 rounded-full shadow-lg">
                  Say "Handle that", "Skip", or "Snooze"
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
}

export default MorningHarvest;
